import { useEffect, useId, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@react-apps/design-system';
import { authContainer } from '@/modules/auth/di/container';
import type { ImpersonationTarget } from './impersonation.store';
import {
  endImpersonationSession,
  type StartedImpersonation,
} from './impersonation-api';
import { ImpersonateModal } from './ImpersonateModal';

/**
 * "Atuar como no MedQ": abre a sessão de suporte com destino no app e a entrega
 * numa aba nova, pelo handoff do próprio ator (a máscara vai no `imp`, e o
 * prazo no `exp`, nunca uma credencial do aluno — decisões 1 e 11 do épico
 * act-as).
 *
 * A aba abre no clique, antes de qualquer await: aberta depois, o bloqueador de
 * pop-up a recusa (molde do `FilePreviewCard`). Ela fica em "Aguardando o
 * motivo…" enquanto o modal está aberto, e um erro do start não a fecha, para o
 * retry do modal ter para onde ir; cancelar fecha.
 */
export function ActAsOnMedqButton({
  target,
  scopeId,
  url,
}: {
  target: ImpersonationTarget;
  scopeId: string;
  url: string | null;
}) {
  const tab = useRef<Window | null>(null);
  const [asking, setAsking] = useState(false);
  const [handingOff, setHandingOff] = useState(false);
  const noUrlHintId = useId();

  // Desmontou com o modal aberto (navegou no admin): a aba ficaria presa em
  // "Aguardando o motivo…". No handoff ela já saiu do ref.
  useEffect(() => () => tab.current?.close(), []);

  const open = () => {
    const opened = window.open('', '_blank');
    if (!opened) {
      toast.error(
        'O navegador bloqueou a nova aba. Permita pop-ups para este site.',
      );
      return;
    }
    opened.opener = null;
    opened.document.title = 'MedQ';
    opened.document.body.textContent = 'Aguardando o motivo…';
    tab.current = opened;
    setAsking(true);
  };

  const cancel = () => {
    tab.current?.close();
    tab.current = null;
    setAsking(false);
  };

  const handOff = async ({ sessionId, expiresAt }: StartedImpersonation) => {
    setAsking(false);
    setHandingOff(true);
    const opened = tab.current;
    tab.current = null;
    // Fechou a aba com o modal aberto: não há onde usar a sessão.
    if (!opened || opened.closed) {
      void endImpersonationSession(sessionId).catch(() => undefined);
      setHandingOff(false);
      return;
    }
    try {
      const { customToken } =
        await authContainer.repository.createHandoffToken();
      if (opened.closed) {
        void endImpersonationSession(sessionId).catch(() => undefined);
        return;
      }
      opened.location.replace(
        `${url}/handoff#ct=${encodeURIComponent(customToken)}&imp=${encodeURIComponent(sessionId)}&exp=${encodeURIComponent(expiresAt)}`,
      );
    } catch {
      // Sem o handoff a sessão nunca seria usada, e ficaria aberta por 1h.
      void endImpersonationSession(sessionId).catch(() => undefined);
      opened.close();
      toast.error('Não foi possível abrir o MedQ. Tente de novo.');
    } finally {
      setHandingOff(false);
    }
  };

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        onClick={open}
        disabled={!url || asking || handingOff}
        aria-label={
          handingOff
            ? 'Abrindo o MedQ…'
            : 'Atuar como no MedQ (abre em nova aba)'
        }
        aria-describedby={url ? undefined : noUrlHintId}
      >
        {handingOff ? 'Abrindo o MedQ…' : 'Atuar como no MedQ'}
      </Button>
      {!url && (
        <span
          id={noUrlHintId}
          className="text-(--color-text-secondary) text-xs"
        >
          O MedQ não tem endereço neste ambiente.
        </span>
      )}
      {asking && (
        <ImpersonateModal
          target={target}
          scopeId={scopeId}
          onClose={cancel}
          onStarted={(session) => void handOff(session)}
        />
      )}
    </>
  );
}
